import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Upload,
  CheckCircle,
  AlertCircle,
  Loader2,
  RotateCcw,
  Wifi,
  WifiOff,
  Zap,
  Camera,
  Clock,
  TrendingUp,
  Shield,
  ShieldCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Trans, t } from "@lingui/macro";
import { useLingui } from "@lingui/react";

interface PhotoUploadStatus {
  id: string;
  name: string;
  status: "pending" | "uploading" | "completed" | "failed";
  progress: number;
  size?: number;
  uploadSpeed?: number;
  retryCount?: number;
}

interface EnhancedUploadProgressProps {
  current: number;
  total: number;
  currentPhoto: string;
  status: "preparing" | "uploading" | "registering" | "completed" | "error";
  className?: string;
  failedPhotos?: string[];
  retryCount?: number;
  onRetryFailedPhotos?: () => void;
  onContinue?: () => void;
  // Enhanced tracking
  individualProgress?: Record<
    string,
    {
      status: "pending" | "uploading" | "completed" | "failed";
      progress: number;
    }
  >;
  uploadMode?: "sequential" | "parallel";
  uploadSpeed?: number;
  estimatedTimeRemaining?: number;
  totalSize?: number;
  uploadedSize?: number;
  startTime?: number;
}

export function EnhancedUploadProgress({
  current,
  total,
  currentPhoto,
  status,
  className,
  failedPhotos = [],
  retryCount = 0,
  onRetryFailedPhotos,
  onContinue,
  individualProgress = {},
  uploadMode = "sequential",
  uploadSpeed = 0,
  estimatedTimeRemaining = 0,
  totalSize = 0,
  uploadedSize = 0,
  startTime = Date.now(),
}: EnhancedUploadProgressProps) {
  const { _ } = useLingui();
  const [networkQuality, setNetworkQuality] = useState<
    "fast" | "slow" | "offline"
  >("fast");
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [animateSuccess, setAnimateSuccess] = useState(false);
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [lastRealProgress, setLastRealProgress] = useState(0);

  const actualProgress = total > 0 ? (current / total) * 100 : 0;

  // Update elapsed time
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(Date.now() - startTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  // Smooth progress animation for better UX during parallel uploads
  useEffect(() => {
    let animationFrame: number;
    let progressInterval: NodeJS.Timeout;

    if (status === "uploading" && uploadMode === "parallel") {
      // For parallel uploads, show gradual progress even when photos upload simultaneously
      if (actualProgress === 0 && lastRealProgress === 0) {
        // Start with gradual progress simulation
        progressInterval = setInterval(
          () => {
            setSmoothProgress((prev) => {
              if (prev < 85) {
                // Don't go beyond 85% until we get real completion
                return prev + Math.random() * 8 + 2; // Random increment between 2-10%
              }
              return prev;
            });
          },
          800 + Math.random() * 400,
        ); // Randomized intervals between 800-1200ms
      } else if (actualProgress > lastRealProgress) {
        // Real progress update - smoothly transition to actual progress
        setLastRealProgress(actualProgress);

        const animateToTarget = () => {
          setSmoothProgress((prev) => {
            if (prev < actualProgress) {
              const diff = actualProgress - prev;
              const increment = Math.max(diff * 0.15, 1); // Smooth catch-up
              return Math.min(prev + increment, actualProgress);
            }
            return actualProgress;
          });

          if (smoothProgress < actualProgress) {
            animationFrame = requestAnimationFrame(animateToTarget);
          }
        };

        animateToTarget();
      }
    } else {
      // For non-parallel uploads or other states, use actual progress
      setSmoothProgress(actualProgress);
      setLastRealProgress(actualProgress);
    }

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
      if (progressInterval) clearInterval(progressInterval);
    };
  }, [actualProgress, status, uploadMode, smoothProgress, lastRealProgress]);

  // Monitor network quality
  useEffect(() => {
    const updateNetworkQuality = () => {
      if (!navigator.onLine) {
        setNetworkQuality("offline");
        return;
      }

      const connection =
        (navigator as any).connection ||
        (navigator as any).mozConnection ||
        (navigator as any).webkitConnection;
      if (connection) {
        const effectiveType = connection.effectiveType;
        if (
          effectiveType === "slow-2g" ||
          effectiveType === "2g" ||
          effectiveType === "3g"
        ) {
          setNetworkQuality("slow");
        } else {
          setNetworkQuality("fast");
        }
      }
    };

    updateNetworkQuality();
    window.addEventListener("online", updateNetworkQuality);
    window.addEventListener("offline", updateNetworkQuality);

    return () => {
      window.removeEventListener("online", updateNetworkQuality);
      window.removeEventListener("offline", updateNetworkQuality);
    };
  }, []);

  // Trigger success animation
  useEffect(() => {
    if (status === "completed" && failedPhotos.length === 0) {
      setAnimateSuccess(true);
      setTimeout(() => setAnimateSuccess(false), 2000);
    }
  }, [status, failedPhotos.length]);

  const formatTimeRemaining = (seconds: number) => {
    if (seconds < 60) return _(t`${Math.ceil(seconds)}s remaining`);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return _(t`${minutes}m ${Math.ceil(remainingSeconds)}s remaining`);
  };

  const formatElapsedTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return _(t`${seconds}s`);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return _(t`${minutes}m ${remainingSeconds}s`);
  };

  const formatSize = (mb: number) => {
    if (mb < 1) return _(t`${Math.round(mb * 1024)}KB`);
    return _(t`${mb.toFixed(1)}MB`);
  };

  const brandIcon = "h-[22px] w-[22px] text-[#166B93]";
  const successIcon = "h-[22px] w-[22px] text-emerald-600";

  const getStatusIcon = () => {
    switch (status) {
      case "preparing":
        return <Loader2 className={cn(brandIcon, "animate-spin")} />;
      case "uploading":
        return uploadMode === "parallel" ? (
          <div className="relative flex h-[22px] w-[22px] items-center justify-center">
            <Upload className={brandIcon} />
            <span className="absolute -right-0.5 -top-0.5 flex h-2 w-2 rounded-full bg-[#166B93] ring-2 ring-white animate-pulse" />
          </div>
        ) : (
          <Upload className={cn(brandIcon, "animate-pulse")} />
        );
      case "registering":
        /* Finalizing on server — shield reads as “securing”, not “still loading” at 100% */
        return (
          <ShieldCheck
            className={cn(brandIcon, "animate-pulse")}
            aria-hidden
          />
        );
      case "completed":
        return animateSuccess ? (
          <CheckCircle className={cn(successIcon, "animate-bounce")} />
        ) : (
          <CheckCircle className={successIcon} />
        );
      case "error":
        return <AlertCircle className="h-[22px] w-[22px] text-red-500" />;
      default:
        return <Upload className="h-[22px] w-[22px] text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "preparing":
        return networkQuality === "offline"
          ? _(t`📡 Offline - photos will upload when connection returns`)
          : networkQuality === "slow"
            ? _(t`🚀 Optimizing for slow connection...`)
            : _(t`⚡ Preparing high-quality upload...`);
      case "uploading":
        if (
          uploadMode === "parallel" &&
          Object.keys(individualProgress).length > 0
        ) {
          const completed = Object.values(individualProgress).filter(
            (p) => p.status === "completed",
          ).length;
          const uploading = Object.values(individualProgress).filter(
            (p) => p.status === "uploading",
          ).length;
          let baseText = _(t`🔄 Uploading ${uploading} photos simultaneously (${completed}/${total} done)`);

          if (uploadSpeed > 0 && estimatedTimeRemaining > 0) {
            baseText += `\n${_(t`💨 ${uploadSpeed.toFixed(1)} MB/s • ${formatTimeRemaining(estimatedTimeRemaining)}`)}`;
          }
          return baseText;
        } else {
          let baseText = _(t`📤 ${currentPhoto}`);
          if (uploadSpeed > 0) {
            baseText += ` • ${uploadSpeed.toFixed(1)} MB/s`;
          }
          return baseText;
        }
      case "registering":
        return _(t`🔐 Securing your uploads and generating access links...`);
      case "completed":
        if (failedPhotos.length > 0) {
          return _(t`⚠️ ${failedPhotos.length} photo${failedPhotos.length > 1 ? "s" : ""} need${failedPhotos.length === 1 ? "s" : ""} attention`);
        }
        return _(t`🎉 All photos uploaded successfully!`);
      case "error":
        return networkQuality === "offline"
          ? _(t`📡 Upload paused - check your internet connection`)
          : _(t`❌ Upload process failed. Please try again.`);
      default:
        return _(t`Processing photos...`);
    }
  };

  const getQualityInsight = () => {
    if (totalSize === 0) return null;

    const avgPhotoSize = totalSize / total;
    if (avgPhotoSize > 5) {
      return _(t`📸 High resolution photos detected - excellent for dental analysis!`);
    } else if (avgPhotoSize > 2) {
      return _(t`✨ Good quality photos - perfect for professional review!`);
    } else {
      return _(t`👍 Photos are ready for upload!`);
    }
  };

  const photoStats: PhotoUploadStatus[] = Object.entries(
    individualProgress,
  ).map(([id, progress]) => ({
    id,
    name: getPhotoDisplayName(id),
    status: progress.status,
    progress: progress.progress,
  }));

  function getPhotoDisplayName(photoId: string): string {
    const names: Record<string, string> = {
      upper_occlusal: _(t`👆 Upper Teeth`),
      lower_occlusal: _(t`👇 Lower Teeth`),
      frontal_retracted_apart: _(t`😮 Front Open`),
      frontal_in_occlusion: _(t`😊 Front Closed`),
      right_buccal: _(t`➡️ Right Side`),
      left_buccal: _(t`⬅️ Left Side`),
    };
    return names[photoId] || photoId;
  }

  return (
    <Card
      className={cn(
        "w-full max-w-lg mx-auto overflow-hidden border border-gray-200/90 bg-white shadow-[0_8px_40px_-12px_rgba(22,107,147,0.25)]",
        className,
      )}
    >
      <CardContent className="space-y-5 p-5 sm:p-6">
        {/* Header — icon in fixed tile for alignment with title */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#166B93]/[0.08] ring-1 ring-[#166B93]/15"
              aria-hidden
            >
              {getStatusIcon()}
            </div>
            <div className="min-w-0 pt-0.5">
              <h3 className="text-base font-semibold leading-tight text-gray-900">
                {uploadMode === "parallel"
                  ? _(t`Smart Photo Upload`)
                  : _(t`Photo Upload`)}
              </h3>
              <p className="mt-1 text-sm leading-snug text-gray-500">
                {uploadMode === "parallel"
                  ? _(t`All photos uploading simultaneously`)
                  : _(t`Processing your dental photos`)}
              </p>
            </div>
          </div>

          <div
            className={cn(
              "flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold",
              networkQuality === "fast"
                ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/80"
                : networkQuality === "slow"
                  ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200/80"
                  : "bg-red-50 text-red-800 ring-1 ring-red-200/80",
            )}
          >
            {networkQuality === "offline" ? (
              <WifiOff className="h-3 w-3" />
            ) : (
              <Wifi className="h-3 w-3" />
            )}
            {networkQuality === "fast"
              ? _(t`Fast`)
              : networkQuality === "slow"
                ? _(t`Slow`)
                : _(t`Offline`)}
          </div>
        </div>

        {/* Status + quality — single cohesive panel */}
        <div className="rounded-2xl border border-[#166B93]/15 bg-gradient-to-b from-[#166B93]/[0.07] to-[#166B93]/[0.02] p-4 sm:p-5">
          <p className="text-sm leading-relaxed text-gray-800 whitespace-pre-line">
            {getStatusText()}
          </p>
          {retryCount > 0 && (
            <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-amber-800">
              <RotateCcw className="h-3.5 w-3.5 shrink-0" />
              <Trans>Retrying failed uploads ({retryCount}/3)</Trans>
            </p>
          )}
          {getQualityInsight() && (
            <p className="mt-4 border-t border-[#166B93]/10 pt-4 text-xs leading-relaxed text-gray-600">
              {getQualityInsight()}
            </p>
          )}
        </div>

        {/* Progress */}
        <div className="space-y-3">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-medium text-gray-700">
              <Trans>Progress</Trans>
            </span>
            <span className="text-lg font-bold tabular-nums text-[#166B93]">
              {Math.round(smoothProgress)}%
            </span>
          </div>
          <div className="relative">
            <Progress
              value={smoothProgress}
              className="h-3.5 transition-all duration-500"
            />
            {uploadMode === "parallel" && status === "uploading" && (
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
                <div
                  className="h-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-pulse"
                  style={{
                    width: "28%",
                    marginLeft: `${Math.max(0, smoothProgress - 12)}%`,
                  }}
                />
              </div>
            )}
          </div>
          <div className="flex justify-between gap-2 text-xs text-gray-500">
            <span className="min-w-0">
              {uploadMode === "parallel" && status === "uploading" ? (
                smoothProgress < 90 ? (
                  <span className="flex flex-wrap items-center gap-x-1 gap-y-0.5">
                    <span className="animate-pulse text-gray-600">
                      <Trans>Processing...</Trans>
                    </span>
                    <span className="text-[#166B93]">
                      ({Math.min(Math.floor(smoothProgress / 17), total)}{" "}
                      <Trans>of</Trans> {total} <Trans>photos</Trans>)
                    </span>
                  </span>
                ) : (
                  <span className="text-gray-600">
                    {current} <Trans>of</Trans> {total}{" "}
                    <Trans>photos</Trans>
                  </span>
                )
              ) : (
                <span className="text-gray-600">
                  {current} <Trans>of</Trans> {total} <Trans>photos</Trans>
                </span>
              )}
            </span>
            <span className="flex shrink-0 items-center gap-1 text-gray-500">
              <Clock className="h-3.5 w-3.5 text-gray-400" />
              {formatElapsedTime(elapsedTime)} <Trans>elapsed</Trans>
            </span>
          </div>
        </div>

        {/* Individual photo progress for parallel uploads */}
        {uploadMode === "parallel" &&
          Object.keys(individualProgress).length > 0 &&
          status === "uploading" && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-900">
                  <Trans>Photo Status</Trans>
                </h4>
                <div className="flex items-center gap-2">
                  {uploadSpeed > 0 && (
                    <div className="flex items-center gap-1 text-xs font-medium text-[#166B93]">
                      <TrendingUp className="h-3 w-3" />
                      {uploadSpeed.toFixed(1)} MB/s
                    </div>
                  )}
                  {smoothProgress < 90 && status === "uploading" && (
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-[#166B93]" />
                      <Trans>Optimizing</Trans>
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {photoStats.map((photo) => (
                  <div
                    key={photo.id}
                    className={cn(
                      "flex items-center gap-2 p-2 rounded-lg text-xs transition-all duration-300",
                      photo.status === "completed"
                        ? "scale-[1.02] bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200/60"
                        : photo.status === "uploading"
                          ? "animate-pulse bg-[#166B93]/10 text-[#166B93] ring-1 ring-[#166B93]/20"
                          : photo.status === "failed"
                            ? "bg-red-50 text-red-700"
                            : "bg-gray-50 text-gray-600",
                    )}
                  >
                    {photo.status === "pending" && (
                      <Camera className="w-3 h-3 opacity-60" />
                    )}
                    {photo.status === "uploading" && (
                      <Loader2 className="h-3 w-3 animate-spin text-[#166B93]" />
                    )}
                    {photo.status === "completed" && (
                      <CheckCircle className="w-3 h-3 animate-bounce" />
                    )}
                    {photo.status === "failed" && (
                      <AlertCircle className="w-3 h-3" />
                    )}
                    <span className="flex-1 truncate font-medium">
                      {photo.name}
                    </span>
                    {photo.status === "completed" && (
                      <span className="text-xs opacity-75">✨</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

        {/* Performance stats toggle */}
        {(uploadSpeed > 0 || totalSize > 0) && (
          <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
            <div className="flex min-w-0 items-center gap-2 text-xs text-gray-600">
              <Shield className="h-3.5 w-3.5 shrink-0 text-[#166B93]/80" />
              <span className="leading-snug">
                <Trans>Secure encrypted upload</Trans>
              </span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowDetailedStats(!showDetailedStats)}
              className="h-9 shrink-0 rounded-full border-gray-200 px-3 text-xs font-semibold text-gray-800 hover:bg-gray-50"
            >
              <Zap className="mr-1.5 h-3.5 w-3.5 text-[#166B93]" />
              <Trans>Stats</Trans>
            </Button>
          </div>
        )}

        {/* Detailed stats panel */}
        {showDetailedStats && (uploadSpeed > 0 || totalSize > 0) && (
          <div className="space-y-2 rounded-xl border border-gray-100 bg-gray-50/90 p-4 text-xs">
            <div className="grid grid-cols-2 gap-3">
              {uploadSpeed > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600"><Trans>Speed:</Trans></span>
                  <span className="font-semibold text-[#166B93]">
                    {uploadSpeed.toFixed(1)} MB/s
                  </span>
                </div>
              )}
              {estimatedTimeRemaining > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600"><Trans>ETA:</Trans></span>
                  <span className="font-semibold text-amber-800">
                    {formatTimeRemaining(estimatedTimeRemaining)}
                  </span>
                </div>
              )}
              {totalSize > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600"><Trans>Total Size:</Trans></span>
                  <span className="font-medium text-gray-900">{formatSize(totalSize)}</span>
                </div>
              )}
              {uploadedSize > 0 && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-600"><Trans>Uploaded:</Trans></span>
                  <span className="font-semibold text-emerald-700">
                    {formatSize(uploadedSize)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Retry prompt for failed uploads */}
        {status === "completed" && failedPhotos.length > 0 && (
          <div className="border border-orange-200 bg-orange-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-orange-900 mb-1">
                  <Trans>Upload Issues Detected</Trans>
                </h4>
                <p className="text-xs text-orange-700 mb-2">
                  <Trans>
                    {failedPhotos.length} photo
                    {failedPhotos.length > 1 ? "s" : ""} couldn't be uploaded:{" "}
                    {failedPhotos.map(getPhotoDisplayName).join(", ")}
                  </Trans>
                </p>
                <p className="text-xs text-orange-600">
                  <Trans>
                    This is usually due to network issues. You can retry or
                    continue - your photos are saved locally.
                  </Trans>
                </p>
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              {onRetryFailedPhotos && (
                <Button
                  onClick={onRetryFailedPhotos}
                  size="sm"
                  variant="outline"
                  className="text-orange-700 border-orange-300 hover:bg-orange-100"
                >
                  <RotateCcw className="w-3 h-3 mr-1" />
                  <Trans>Retry Upload</Trans>
                </Button>
              )}
              {onContinue && (
                <Button
                  onClick={onContinue}
                  size="sm"
                  variant="outline"
                  className="text-gray-700 border-gray-300 hover:bg-gray-100"
                >
                  <Trans>Continue Anyway</Trans>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Success message */}
        {status === "completed" && failedPhotos.length === 0 && (
          <div className="rounded-2xl border border-emerald-200/80 bg-gradient-to-b from-emerald-50 to-white p-5 text-center shadow-sm">
            <CheckCircle
              className={cn(
                "mx-auto mb-3 h-9 w-9 text-emerald-600",
                animateSuccess && "animate-bounce",
              )}
            />
            <p className="text-sm font-semibold text-emerald-950">
              <Trans>Perfect! All photos uploaded successfully</Trans>
            </p>
            <p className="mt-2 text-xs leading-relaxed text-emerald-900/80">
              <Trans>
                Your high-quality dental photos are now securely stored and ready
                for professional analysis.
              </Trans>
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
